'use client';

import { getList } from '@/requests/api/get-list';
import {
  MAX_PAGE_SIZE,
  NESTED_KEY_SEPARATOR,
  QUERY_KEYS,
} from '@/requests/constants';
import { useGetList } from '@/requests/hooks';
import { filterNested, mergeDataWithNested } from '@/requests/hooks/utils';
import type { ListResponse, QueryParams } from '@/requests/types';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import {
  useQueries,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { customCaseHandlers } from './custom-cases';
import { useFetchNestedQueries } from './use-fetch-nested';

export function useFetchAllInstances<
  TInstance extends BaseInstance,
  TransformedData = ListResponse<TInstance>,
>(
  options: Omit<
    UseQueryOptions<ListResponse<TInstance>['list']>,
    'queryKey' | 'queryFn'
  > & {
    modelName: ModelName;
    order?: QueryParams['order'];
    filters?: QueryParams<TInstance>['filters'];
    nested?: (keyof TInstance)[];
    overrideUrl?: string;
    overrideUrlReplaceAll?: boolean;
    slim?: boolean;
    enabled?: boolean;
    search?: string;
    fetchAllManyRelations?: boolean;
    waitForAllNested?: boolean;
    transform?: (list: ListResponse<TInstance>) => TransformedData;
    enableGlobalFilters?: boolean;
  }
) {
  const {
    modelName,
    order,
    filters,
    nested = [],
    overrideUrlReplaceAll = false,
    slim,
    fetchAllManyRelations = false,
    enabled,
    search,
    overrideUrl,
    waitForAllNested,
    transform = (list) => list,
    enableGlobalFilters,
  } = options;

  /**
   * Filter out fields that should never be fetched to avoid unnecessary data fetching.
   */
  const nestedKey = nested.join(NESTED_KEY_SEPARATOR);
  const filteredNested = useMemo(
    () => filterNested<TInstance>(nestedKey),
    [nestedKey]
  );

  const countData = useGetList({
    modelName: options.modelName,
    overrideUrl,
    overrideUrlReplaceAll,
    queryParams: {
      filters: options.filters,
      enableGlobalFilters,
      page: 1,
      pageSize: 1,
      order,
      slim: true,
      search,
    },
    enabled,
  });

  const totalSize = countData.data?.meta?.total_amount ?? 0;

  const pageAmount = totalSize
    ? totalSize < 500
      ? 1
      : Math.ceil(totalSize / 500)
    : 0;

  const batchedQueries = Array.from({ length: pageAmount }).map((_, page) => {
    return {
      queryKey: [
        modelName,
        QUERY_KEYS.ALL_INSTANCES,
        filters,
        search,
        slim,
        order,
        fetchAllManyRelations,
        page + 1,
        enableGlobalFilters,
      ],
      queryFn: async () => {
        const res = await getList<TInstance>({
          modelName,
          overrideUrl,
          overrideUrlReplaceAll,
          queryParams: {
            page: page + 1,
            pageSize: MAX_PAGE_SIZE,
            filters,
            search,
            order,
            slim,
            enableGlobalFilters,
          },
        });
        return res.list;
      },
      ...options,
    };
  });

  const combine = useCallback(
    (results: UseQueryResult<TInstance[], Error>[]) => {
      const instances =
        results
          .flatMap((result) => result.data)
          .filter((res) => res !== undefined) ?? [];
      return {
        data: {
          list: instances,
          meta: {
            total_amount: instances.length,
            page: 1,
            page_amount: instances.length,
            page_size: instances.length,
          },
        },
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        isFetching: results.some((result) => result.isFetching),
        isPending: results.some((result) => result.isPending),
        isRefetching: results.some((result) => result.isRefetching),
        isStale: results.every((result) => result.isStale),
        isSuccess: results.every((result) => result.isSuccess),
      };
    },
    []
  );

  const mainQueries = useQueries({
    queries: batchedQueries,
    combine: combine,
  });

  const nestedQueries = useFetchNestedQueries(
    modelName,
    mainQueries.data.list ?? [],
    filteredNested,
    fetchAllManyRelations
  );

  /**
   * Merge the aggregated nested data with the main list data.
   */
  const mergedData = useMemo(() => {
    if (!mainQueries.data || Object.keys(nestedQueries.data).length < 1) {
      return mainQueries.data;
    }
    return mergeDataWithNested(
      mainQueries.data,
      nestedQueries.data,
      filteredNested
    );
  }, [filteredNested, mainQueries.data, nestedQueries.data]);

  const customizedDataResult = useQuery({
    queryKey: ['customizedData', modelName, JSON.stringify(mergedData)],
    queryFn: async () => {
      const handler = customCaseHandlers[modelName];
      if (!handler) {
        return mergedData;
      }
      try {
        const processedList = await handler({ data: mergedData?.list ?? [] });
        return {
          list: processedList.data,
          meta: mergedData?.meta,
        } as ListResponse<TInstance>;
      } catch {
        return mergedData;
      }
    },
    enabled: !!mergedData?.list.length && !!customCaseHandlers[modelName],
  });

  /**
   * Determine if there is a custom case handler for the model.
   */
  const hasCustomCaseHandler = !!customCaseHandlers[modelName];

  const transformedData = useMemo(() => {
    if (hasCustomCaseHandler && customizedDataResult.data) {
      return transform(customizedDataResult.data) as TransformedData;
    }
    return transform(mergedData) as TransformedData;
  }, [customizedDataResult.data, hasCustomCaseHandler, mergedData, transform]);

  /**
   * Combine the loading states based on the `waitForAllNested` flag.
   */
  const isLoading =
    countData.isLoading || waitForAllNested
      ? mainQueries.isLoading ||
        nestedQueries.isLoading ||
        customizedDataResult.isLoading ||
        countData.isLoading
      : mainQueries.isLoading;
  const isFetching =
    countData.isFetching || waitForAllNested
      ? mainQueries.isFetching ||
        nestedQueries.isFetching ||
        customizedDataResult.isFetching
      : mainQueries.isFetching;
  const isPending =
    countData.isPending || waitForAllNested
      ? mainQueries.isPending ||
        nestedQueries.isPending ||
        customizedDataResult.isPending
      : mainQueries.isPending;
  const isAllSuccess =
    countData.isSuccess && waitForAllNested
      ? mainQueries.isSuccess &&
        nestedQueries.isSuccess &&
        // This won't be fired on all requests with waitForAllNested, will cause
        // isAllSuccess to always be false when not used
        (customizedDataResult.isFetched ? customizedDataResult.isSuccess : true)
      : mainQueries.isSuccess;

  return {
    data: mergedData,
    transformedData,
    isLoading: isLoading || countData.isLoading,
    isFetching: isFetching || countData.isFetching,
    isPending: isPending || countData.isPending,
    isSuccess: mainQueries.isSuccess,
    isAllSuccess,
    isError: nestedQueries.isError || mainQueries.isError || countData.isError,
    isRefetching:
      nestedQueries.isRefetching ||
      mainQueries.isRefetching ||
      countData.isRefetching,
    isStale: nestedQueries.isStale && mainQueries.isStale && countData.isStale,
  };
}
